import * as React from "react";
import { logger } from "bernie-core";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { StorefrontWizardRegionCOMETProps } from "./typings";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { CustomerNotificationLocation } from "@shared-ui/customer-notifications";
import { withLocalization } from "@shared-ui/localization-context";
import { LobQueryRouter } from "src/components/shared/StorefrontWizard/LobQueryRouter";
import {
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
  getNonWizardFormChildren,
} from "src/components/shared/StorefrontWizard/StorefrontWizardUtils";
import { TabbedWizardCard } from "src/components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { CustomerNotifications } from "components/shared/CustomerNotifications";
import { withStores } from "src/stores";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";

@withLocalization
@logger("StorefrontWizardRegionCOMET")
class StorefrontWizardRegionCOMET extends React.Component<StorefrontWizardRegionCOMETProps> {
  public render() {
    const { flexComponent, context, wizardState, compositionStore } = this.props;
    const blossomComponent = BlossomComponentFactory.get(this.props as BlossomComponentFactoryProps);

    //default to false if device information is not available
    wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

    if (!flexComponent || !flexComponent.model || !blossomComponent) {
      return null;
    }

    wizardState.globalWizardState.config.useLastSearchForMultiLOB = flexComponent.model.useLastSearchForMultiLOB;

    const { defaultLOB, cdgTreatment } = flexComponent.model;

    if (defaultLOB) {
      wizardState.globalWizardState.config.navigation.defaultLob = defaultLOB;
      wizardState.globalWizardState.config.navigation.activeLob = defaultLOB;
    }

    wizardState.globalWizardState.setLastSearchedLob();

    const children = blossomComponent.renderChildren(this.props.logger, this.props.l10n, context);

    if (children.length < 1) {
      return null;
    }

    const wizardLOBs = getWizardLOBFlexComponents(flexComponent.modules);

    const wizardFormChildren = getWizardLOBFormChildren(children);

    const otherFormChildren = getNonWizardFormChildren(children);

    wizardState.globalWizardState.config.heroImageSrc = flexComponent.model.backgroundImage
      ? flexComponent.model.backgroundImage
      : "https://forever.travel-assets.com/flex/flexmanager/images/2020/06/26/Orbitz-wizard-background.jpg";
    const imageSrcTemp = this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          wizardState.globalWizardState.config.heroImageSrc,
          1400,
          817,
          context,
          AspectRatioEnum.DEFAULT,
          true,
          80
        )
      : wizardState.globalWizardState.config.heroImageSrc;

    const isStorefrontPage = context.searchContext.pageType.indexOf("Storefront") > -1;
    const cometSFPadding =
      flexComponent.model.brandModulePadding && flexComponent.model.brandModulePadding === "comet_padding"
        ? "comet-homepage-module"
        : "";

    const subHeading =
      flexComponent.model.innerTitle ||
      (compositionStore.composition!.pageSubHeadline && flexComponent.model.subTitleFromPageSubHeader);
    const heading =
      flexComponent.model.title ||
      (compositionStore.composition!.pageHeading && flexComponent.model.titleFromPageHeader);

    const headingClassName = `SimpleContainer cometHeading all-x-padding-six all-t-padding-six  ${
      subHeading ? "s-b-padding-three" : "s-b-padding-six"
    }`;
    const headingElement = (isMobile: boolean) => (
      <h1 className={`${headingClassName} uitk-type-${cdgTreatment && isMobile ? "heading-600" : "display-800"}`}>
        {flexComponent.model.titleFromPageHeader
          ? compositionStore.composition!.pageHeading
          : flexComponent.model.title}
      </h1>
    );

    const classNamesForSingleLobWizard = wizardFormChildren.length === 1 ? "all-t-padding-six s-t-padding-three" : "";

    /**
     *
     * Note for modules below this region:
     * Modules not part of this region do not align to the form content out of the box
     * Setting the flex manager attribute brandModulePadding to comet_padding puts a spacing__twelve on the components below this region
     * This aligns them with the form content within the card on this wizard
     */
    return (
      <>
        <StorefrontWizardRegionTypesContext.Provider value={StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_COMET}>
          <div className={`StorefrontWizardRegionCOMET ${cometSFPadding}`} data-testid="storefront-region">
            <div data-testid="main-region" className="cometRegion">
              <UitkFigure className="cometBackgroundImageFigure">
                <UitkImage
                  src={imageSrcTemp}
                  className={cdgTreatment ? "" : "cometImageBackground"}
                  alt={flexComponent.model.title}
                />
                {cdgTreatment && <UitkScrim type={UitkScrimType.OVERLAY} />}
                {heading && (
                  <Viewport>
                    <ViewSmall>{headingElement(true)}</ViewSmall>
                    <ViewMedium>{headingElement(false)}</ViewMedium>
                  </Viewport>
                )}
                {heading && subHeading && (
                  <h2 className="SimpleContainer cometHeading uitk-type-subheading all-x-padding-six all-t-padding-three s-t-padding-zero s-b-padding-three">
                    {flexComponent.model.subTitleFromPageSubHeader
                      ? compositionStore.composition!.pageSubHeadline
                      : flexComponent.model.innerTitle}
                  </h2>
                )}
                <LobQueryRouter lobs={wizardLOBs} />
                <Viewport>
                  <ViewSmall>
                    <>
                      {isStorefrontPage && (
                        <CustomerNotifications
                          location={CustomerNotificationLocation.AFTER_HEADER}
                          className="customerNotificationSquare"
                        />
                      )}
                      <TabbedWizardCard
                        wizardFormChildren={wizardFormChildren}
                        wizardState={wizardState}
                        wrappingClassNames="SimpleContainer mobileCardRadius"
                        cardClassNames="all-cell-1-1"
                        cardContentClassNames={`uitk-cell lobFormInCard ${classNamesForSingleLobWizard}`}
                      />
                    </>
                  </ViewSmall>
                  <ViewMedium>
                    <>
                      {isStorefrontPage && (
                        <CustomerNotifications
                          location={CustomerNotificationLocation.AFTER_HEADER}
                          className="customerNotificationSquare all-x-padding-six all-t-padding-twelve"
                        />
                      )}
                      <TabbedWizardCard
                        wizardFormChildren={wizardFormChildren}
                        wizardState={wizardState}
                        wrappingClassNames="all-t-padding-six all-x-padding-six SimpleContainer"
                        cardClassNames="all-cell-1-1"
                        cardContentClassNames={`uitk-cell lobFormInCard ${classNamesForSingleLobWizard}`}
                        tabGroupClassNames="formAlignedTabs all-x-padding-six"
                        wizardFormClassNames="wizardOverHeroImage all-x-padding-six"
                      />
                    </>
                  </ViewMedium>
                </Viewport>
              </UitkFigure>
              {otherFormChildren}
            </div>
          </div>
        </StorefrontWizardRegionTypesContext.Provider>
      </>
    );
  }
}

export default withStores("wizardState", "thumbor", "compositionStore")(StorefrontWizardRegionCOMET);
